import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageCircle, FileText, Clock, MapPin, ArrowRight, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { QuoteWizard } from "@/components/QuoteWizard";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const API_BASE = (api.defaults.baseURL || "").replace(/\/api$/, "");

function injectHead(slug, c, base, locale) {
  document.title = c.meta_title || c.title;
  const meta = (name, content) => {
    let el = document.head.querySelector(`meta[name="${name}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); el.dataset.seo = "1"; document.head.appendChild(el); }
    el.setAttribute("content", content);
  };
  meta("description", c.meta_description);
  // hreflang + canonical
  document.head.querySelectorAll("link[data-seo='1'],script[data-seo='1']").forEach((e) => e.remove());
  ["it", "ro", "en"].forEach((loc) => {
    const l = document.createElement("link");
    l.rel = "alternate"; l.hreflang = loc; l.href = `${base}/${slug}?lang=${loc}`; l.dataset.seo = "1";
    document.head.appendChild(l);
  });
  const xd = document.createElement("link");
  xd.rel = "alternate"; xd.hreflang = "x-default"; xd.href = `${base}/${slug}`; xd.dataset.seo = "1";
  document.head.appendChild(xd);
}

function injectSchema(slug, c, base, originName, destName) {
  document.querySelectorAll("script[data-seoschema='1']").forEach((e) => e.remove());
  const add = (obj) => {
    const s = document.createElement("script");
    s.type = "application/ld+json"; s.dataset.seoschema = "1";
    s.text = JSON.stringify(obj); document.head.appendChild(s);
  };
  add({ "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: (c.faq || []).map((f) => ({ "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a } })) });
  add({ "@context": "https://schema.org", "@type": "Service", serviceType: "Funeral repatriation",
    name: c.h1, description: c.meta_description, areaServed: [originName, destName],
    provider: { "@type": "Organization", name: "Rimpatrio24", url: base } });
  add({ "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: base },
      { "@type": "ListItem", position: 2, name: c.h1, item: `${base}/${slug}` },
    ] });
}

export default function SeoLanding() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/public/seo-page/${slug}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
    api.get("/public/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const c = data.content[locale] || data.content.it;
    const base = data.base_url || window.location.origin;
    injectHead(slug, c, base, locale);
    injectSchema(slug, c, base, data.origin.name, data.destination.name);
    return () => { document.querySelectorAll("[data-seo='1'],[data-seoschema='1']").forEach((e) => e.remove()); };
  }, [data, locale, slug]);

  useEffect(() => { if (notFound) navigate("/"); }, [notFound, navigate]);

  if (notFound) return null;
  if (!data || !settings) return <div className="min-h-screen flex items-center justify-center text-stone-400">…</div>;

  const c = data.content[locale] || data.content.it;
  const L = {
    docs: { it: "Documenti necessari", ro: "Documente necesare", en: "Required documents", de: "Erforderliche Dokumente", fr: "Documents nécessaires" },
    time: { it: "Tempo di trasporto stimato", ro: "Timp estimat de transport", en: "Estimated transport time", de: "Geschätzte Transportzeit", fr: "Durée de transport estimée" },
    related: { it: "Tratte correlate", ro: "Rute conexe", en: "Related routes", de: "Verwandte Strecken", fr: "Itinéraires associés" },
    allServices: { it: "Tutti i servizi", ro: "Toate serviciile", en: "All services", de: "Alle Leistungen", fr: "Tous les services" },
  };
  const lab = (k) => L[k][locale] || L[k].it;
  const phone = data.emergency_phone || "+39 02 0000 0000";
  const wa = (data.whatsapp || phone).replace(/[^\d]/g, "");
  const pickup = { label: data.origin.name, city: data.origin.city, lat: data.origin.lat, lon: data.origin.lon };
  const dest = { label: data.destination.name, city: data.destination.city, lat: data.destination.lat, lon: data.destination.lon };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="seo-landing">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif-display text-xl">Rimpatrio<span className="text-[#8a6d2f]">24</span></Link>
          <div className="flex items-center gap-3">
            <a href={`tel:${phone.replace(/\s/g, "")}`} className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-blue-900" data-testid="seo-phone"><Phone size={15} /> {phone}</a>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-6 pt-5">
        <nav className="flex items-center gap-1.5 text-xs text-stone-400" data-testid="seo-breadcrumb">
          <Link to="/" className="hover:text-stone-600">Home</Link>
          <ChevronRight size={12} />
          <span className="text-stone-600">{data.origin.name} → {data.destination.name}</span>
        </nav>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-8 grid lg:grid-cols-5 gap-8">
        <article className="lg:col-span-3 space-y-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-serif-display text-3xl lg:text-4xl text-stone-900 leading-tight" data-testid="seo-h1">{c.h1}</h1>
            <p className="text-stone-600 mt-4 text-lg leading-relaxed">{c.content}</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="seo-documents">
              <h2 className="font-semibold text-stone-900 flex items-center gap-2 mb-3"><FileText size={17} className="text-blue-900" /> {lab("docs")}</h2>
              <ul className="space-y-1.5 text-sm text-stone-600">
                {c.documents.map((doc, i) => <li key={i} className="flex gap-2"><span className="text-[#8a6d2f]">•</span> {doc}</li>)}
              </ul>
            </div>
            <div className="bg-white rounded-xl border border-stone-200 p-5" data-testid="seo-transport-time">
              <h2 className="font-semibold text-stone-900 flex items-center gap-2 mb-3"><Clock size={17} className="text-blue-900" /> {lab("time")}</h2>
              <p className="text-2xl font-serif-display text-stone-900">{c.transport_time}</p>
              <p className="text-xs text-stone-400 mt-2 flex items-center gap-1"><MapPin size={12} /> {data.origin.name} → {data.destination.name}</p>
            </div>
          </div>

          {/* FAQ */}
          <div data-testid="seo-faq">
            <h2 className="font-serif-display text-2xl mb-3">FAQ</h2>
            <Accordion type="single" collapsible className="bg-white rounded-xl border border-stone-200 px-4">
              {c.faq.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-stone-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Related routes — internal linking */}
          {data.related.length > 0 && (
            <div data-testid="seo-related">
              <h2 className="font-serif-display text-xl mb-3">{lab("related")}</h2>
              <div className="flex flex-wrap gap-2">
                {data.related.map((r) => (
                  <Link key={r.slug} to={`/${r.slug}`} className="text-sm px-3 py-1.5 rounded-full bg-white border border-stone-200 hover:border-blue-900 hover:text-blue-900 transition-colors" data-testid="seo-related-link">
                    {r.origin_name} → {r.dest_name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>

        {/* Sticky quote wizard + CTA */}
        <aside className="lg:col-span-2">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="bg-gradient-to-br from-blue-950 to-stone-900 rounded-2xl p-5 text-white" data-testid="seo-cta">
              <p className="font-serif-display text-xl leading-snug">{c.cta}</p>
              <div className="flex gap-2 mt-4">
                <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex-1"><Button className="w-full bg-white text-blue-950 hover:bg-stone-100"><Phone size={15} className="mr-1.5" /> {phone}</Button></a>
                <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="seo-whatsapp"><Button className="bg-emerald-600 hover:bg-emerald-500"><MessageCircle size={16} /></Button></a>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-1">
              <QuoteWizard settings={settings} initialPickup={pickup} initialDestination={dest} seoSlug={slug} />
            </div>
          </div>
        </aside>
      </main>

      <footer className="border-t border-stone-200 bg-white mt-8">
        <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-stone-400 flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} Rimpatrio24</span>
          <Link to="/" className="hover:text-stone-600 inline-flex items-center gap-1">{lab("allServices")} <ArrowRight size={13} /></Link>
        </div>
      </footer>
    </div>
  );
}
